import React, { useContext, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button } from "../Button";
import { useTranslation } from "../../generated/localisation";
import { BsBoxArrowUpRight } from "react-icons/bs";
import { buildStripePortalUrl } from "../../lib/buildStripePortalUrl";
import { Site } from "../../contexts";

export interface StripePortalLinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  children: React.ReactNode;
}

/**
 * Link that opens the Stripe customer portal in a new tab. On click, shows a confirmation
 * modal first; the user can cancel or continue (via a standard anchor) to open the portal.
 */
export const StripePortalLink: React.FC<StripePortalLinkProps> = ({
  children,
  className,
  onClick,
  ...rest
}) => {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const site = useContext(Site);
  const href = useMemo(
    () => buildStripePortalUrl(i18n.language),
    [i18n.language],
  );

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowModal(true);
    onClick?.(e);
  };

  return (
    <>
      <a
        {...rest}
        href={href}
        className={className}
        onClick={handleLinkClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("main.stripePortalModalTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ whiteSpace: "pre-line" }} className="mb-0">{t("main.stripePortalModalBody", { siteName: site.name })}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => setShowModal(false)}
          >
            {t("main.cancel")}
          </Button>
          <Button
            href={href}
            variant="solid"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2"
            onClick={() => setShowModal(false)}
          >
            {t("main.stripePortalModalContinue")}
            <BsBoxArrowUpRight className="ml-1" size={14} aria-hidden={true} />
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
